import React from "react";
import { Stack, CircularProgress, Typography, Select, MenuItem } from "@mui/material";
import { parse, format } from "date-fns";

const WEEK_DAYS = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

/**
 * Generates a list of time options for the make-up session.
 * @returns {Object} An object where the keys are military time strings and the values are formatted time strings.
 * @example
 * {
 *   "00:00:00": "12:00 AM",
 *   "00:30:00": "12:30 AM",
 * }
 */
const generateTimeOptions = () => {
  const times = {};

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = parse(`${hour}:${minute}:00`, "HH:mm:ss", new Date());
      times[`${format(time, "HH:mm:ss")}`] = format(time, "h:mm a");
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

const MakeUpSessionsForm = ({
  currentServices,
  editMode,
  loading,
  makeupPreferences,
  setMakeupPreferences,
}) => {
  const { day, time } = makeupPreferences;
  const makeUpDay = currentServices?.makeUpDay;
  const makeUpTime = currentServices?.makeUpTime;

  const handleDayChange = (e) => {
    setMakeupPreferences({
      day: e.target.value,
      time: time ?? makeUpTime,
    });
  };

  const handleTimeChange = (e) => {
    setMakeupPreferences({
      day: day ?? makeUpDay,
      time: e.target.value,
    });
  };

  if (loading) {
    return <CircularProgress size={30} color="primary" style={{ marginBottom: 10 }} />;
  }

  return (
    <Stack spacing={7} direction={"row"}>
      {editMode ? (
        <Stack gap={"8px"}>
          <Typography variant="inputLabel">Make-up Session</Typography>

          <Stack spacing={2} direction="row" gap={"8px"}>
            <Select
              id="day"
              value={day ?? makeUpDay}
              name="day"
              label=""
              onChange={handleDayChange}
              style={{ minWidth: "128px" }}
            >
              <MenuItem value="">Select Day</MenuItem>
              {Object.entries(WEEK_DAYS).map(([key, value]) => (
                <MenuItem key={key} value={key} title={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>

            <Select
              id="time"
              value={time ?? makeUpTime}
              name="time"
              onChange={handleTimeChange}
              style={{ minWidth: "128px", flexGrow: 1 }}
            >
              <MenuItem value="">Select Time</MenuItem>
              {Object.entries(TIME_OPTIONS).map(([key, value]) => (
                <MenuItem key={key} value={key} title={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      ) : (
        <Stack gap={"8px"}>
          <Typography variant="inputLabel">Make-up Session</Typography>
          {makeUpDay && makeUpTime ? (
            <Stack spacing={2} direction={"row"} gap={"8px"}>
              <Typography variant="body2">{WEEK_DAYS[makeUpDay]}</Typography>
              <Typography variant="subtitle2">{TIME_OPTIONS[makeUpTime]}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2">No make-up session scheduled</Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default MakeUpSessionsForm;
